import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthContext";
import { can } from "../lib/roles";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { ListRow, StatusLabel } from "../components/ListTable";
import { Results, ItemCard } from "../components/Paged";
import { inputClass } from "../components/FormKit";
import { Icon } from "../app/Icon";
import { labelize } from "../lib/format";
import {
  adminListTickets, adminGetTicket, adminReplyTicket, adminUpdateTicket,
  TICKET_STATUSES, TICKET_PRIORITIES, type Ticket, type TicketMessage,
} from "../lib/support";

const TABS = TICKET_STATUSES.map((s) => ({ key: s, label: labelize(s) }));

export function AdminSupport() {
  const { roles } = useAuth();
  const isStaff = can(roles, "admin_support");
  const [tab, setTab] = useState("open");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [sel, setSel] = useState<{ ticket: Ticket; messages: TicketMessage[]; me: string } | null>(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);

  async function refreshList(status = tab) {
    if (!isStaff) return;
    const r = await adminListTickets(status);
    setTickets(r.tickets);
    setCounts(r.counts);
  }
  useEffect(() => {
    setSel(null);
    void refreshList(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isStaff]);

  async function open(id: string) {
    setSel(await adminGetTicket(id));
  }
  async function send(e: FormEvent) {
    e.preventDefault();
    if (!sel || !reply.trim()) return;
    setBusy(true);
    try {
      await adminReplyTicket(sel.ticket.id, reply, internal);
      setReply("");
      await open(sel.ticket.id);
      await refreshList();
    } finally {
      setBusy(false);
    }
  }
  async function update(patch: { status?: string; priority?: string }) {
    if (!sel) return;
    await adminUpdateTicket({ ticket_id: sel.ticket.id, ...patch });
    await open(sel.ticket.id);
    await refreshList();
  }

  if (!isStaff) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Support" subtitle="Support access required." />
        <p className="font-mono text-sm text-danger">Support staff only.</p>
      </div>
    );
  }

  // --- Ticket detail ---
  if (sel) {
    const t = sel.ticket;
    return (
      <div className="grid gap-6">
        <button type="button" onClick={() => setSel(null)} className="flex w-max items-center gap-1 font-mono text-sm font-bold text-accent-strong">
          <Icon name="chevron" className="h-4 w-4 rotate-90" /> Queue
        </button>
        <PageHeader title={t.subject} subtitle={`${labelize(t.category)} · ${t.account_email ?? ""}`} />

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start">
          <div className="grid content-start gap-4">
            <div className="grid gap-2 rounded-card border border-line bg-surface p-4">
              {sel.messages.map((m) => (
                <div key={m.id} className={`flex flex-col ${m.is_staff ? "items-end" : "items-start"}`}>
                  <span className="px-1 font-mono text-[10px] font-bold uppercase tracking-widest text-muted">
                    {m.internal ? "Internal note" : m.is_staff ? "Staff" : "User"}
                  </span>
                  <span className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${m.internal ? "border border-accent/40 bg-accent/5 text-ink" : m.is_staff ? "bg-accent text-accent-ink" : "border border-line bg-surface-2"}`}>{m.body}</span>
                  <span className="mt-0.5 px-1 font-mono text-[10px] text-muted">{new Date(m.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <form className="grid gap-2" onSubmit={send}>
              <textarea className="min-h-[3rem] resize-none rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink" rows={3} maxLength={5000} placeholder={internal ? "Internal note (not shown to the user)…" : "Reply to the user…"} aria-label="Reply" value={reply} onChange={(e) => setReply(e.target.value)} />
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={busy || !reply.trim()}>{internal ? "Add note" : "Send reply"}</Button>
                <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Internal note</label>
              </div>
            </form>
          </div>

          <Panel title="Manage">
            <label className="grid gap-1 text-xs font-bold text-muted">Status
              <select className={inputClass} value={t.status} onChange={(e) => update({ status: e.target.value })} aria-label="Status">
                {TICKET_STATUSES.map((s) => <option key={s} value={s}>{labelize(s)}</option>)}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-bold text-muted">Priority
              <select className={inputClass} value={t.priority} onChange={(e) => update({ priority: e.target.value })} aria-label="Priority">
                {TICKET_PRIORITIES.map((p) => <option key={p} value={p}>{labelize(p)}</option>)}
              </select>
            </label>
            <div className="border-t border-line pt-2 font-mono text-xs text-muted">
              <p>Opened {new Date(t.created_at).toLocaleString()}</p>
              <p>Updated {new Date(t.updated_at).toLocaleString()}</p>
            </div>
          </Panel>
        </div>
      </div>
    );
  }

  // --- Queue ---
  return (
    <div className="grid gap-6">
      <PageHeader title="Admin · Support" subtitle="Respond to and triage support tickets." />
      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === t.key ? "border-accent text-accent-strong" : "border-transparent text-muted hover:text-ink"}`}>
            {t.label} ({counts[t.key] ?? 0})
          </button>
        ))}
      </div>
      <Results
        items={tickets}
        perPage={12}
        defaultView="list"
        listColumns={["Subject / user", "Priority", "Updated"]}
        gridClass="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        empty={<p className="font-mono text-sm text-muted">Nothing here.</p>}
        renderRow={(t) => (
          <ListRow key={t.id} onClick={() => open(t.id)} title={t.subject} meta={t.account_email ?? ""} status={t.priority} right={<span className="font-mono text-xs text-muted"><StatusLabel status={t.status} /> · {new Date(t.updated_at).toLocaleDateString()}</span>} />
        )}
        renderCard={(t) => (
          <ItemCard key={t.id} onClick={() => open(t.id)} title={t.subject} meta={t.account_email ?? ""} status={t.status} right={`${labelize(t.priority)} · ${new Date(t.updated_at).toLocaleDateString()}`} />
        )}
      />
    </div>
  );
}
